import { environment } from '@bling-fe/shared/env';
import { ArticleScope } from '@bling-fe/shared/utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { getCurrentUser } from '../user-state/user.selectors';
import { TenantSetting } from '../../declarations/tenant-setting.interface';

import { TenantState } from './tenant.state';

const getFeatureState = createFeatureSelector<TenantState>('tenantState');

export const getCurrentTenant = createSelector(
  getFeatureState,
  (state: TenantState) => state.currentTenant
);

export const getCurrentTenantApiUrl = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return `${state.currentTenant.base_url}/api/v1`;
    } else {
      return environment.apiDomain.replace(/\/$/, '');
    }
  }
);

export const getRepairMessagesEnabled = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state?.currentTenant?.tenant_setting?.repair_messages_enabled;
    } else {
      return false;
    }
  }
);

export const getRepairMessagesCancelEnabled = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.repair_messages_cancel_enabled;
    } else {
      return false;
    }
  }
);

export const getRepairMessagesRequireCancellationReason = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting
        .repair_messages_require_cancellation_reason;
    } else {
      return false;
    }
  }
);

export const getAllowTicketReopenByUser = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.allow_ticket_reopen_by_user;
    } else {
      return false;
    }
  }
);

export const getRepairMessagesMessagingEnabled = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.service_communication_enabled;
    } else {
      return false;
    }
  }
);

export const getInstantMessagingEnabled = createSelector(
  getFeatureState,
  getCurrentUser,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.instant_messaging_enabled;
    } else {
      return false;
    }
  }
);

export const getDailyReservationsCancelEnabled = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting
        .daily_reservations_cancel_enabled;
    } else {
      return false;
    }
  }
);

export const getMembershipPlansUrl = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.membership_plans_url || null;
    } else {
      return null;
    }
  }
);

export const getIhzPredefinedTicketCategoriesIds = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting
        .ihz_predefined_ticket_categories_ids;
    } else {
      return false;
    }
  }
);

export const getCurrentTenantSettings = createSelector(
  getCurrentTenant,
  currentTenant => {
    if (currentTenant !== 'loading' && currentTenant !== null) {
      return currentTenant.tenant_setting;
    }
    return null;
  }
);

export const isPrivilegedUser = (
  user: { admin?: boolean; staff?: boolean; super_admin?: boolean } | null
): boolean => !!(user?.admin || user?.staff || user?.super_admin);

export const residentFeedOriginFor = (
  tenantSettings: TenantSetting | null
): 'my_settlement' | 'all' =>
  tenantSettings?.residents_see_all_posts_by_default ? 'all' : 'my_settlement';

export const getDefaultFeedOrigin = createSelector(
  getCurrentUser,
  getCurrentTenantSettings,
  (currentUser, tenantSettings): 'my_settlement' | 'all' => {
    if (!isPrivilegedUser(currentUser)) {
      return residentFeedOriginFor(tenantSettings);
    }
    return tenantSettings?.filter_by_location_by_default
      ? 'my_settlement'
      : 'all';
  }
);

export const getCookiebotId = createSelector(
  getFeatureState,
  (state: TenantState) => {
    if (state.currentTenant !== 'loading' && state.currentTenant !== null) {
      return state.currentTenant.tenant_setting.cookiebot_id;
    }
    return null;
  }
);

export const getCurrentUserWithTenantSettings = createSelector(
  getCurrentUser,
  getCurrentTenantSettings,
  (currentUser, tenantSettings) => {
    return { currentUser, tenantSettings };
  }
);

export const getTenantCurrency = createSelector(
  getCurrentTenant,
  currentTenant => {
    if (currentTenant !== 'loading' && currentTenant !== null) {
      return currentTenant.tenant_setting?.currency || 'CHF';
    }
    return 'CHF';
  }
);

export const getInvoicePaymentsEnabled = createSelector(
  getCurrentTenant,
  currentTenant => {
    if (currentTenant !== 'loading' && currentTenant !== null) {
      return currentTenant.tenant_setting?.invoice_payments_enabled ?? false;
    }
    return false;
  }
);

export const getRepairMessagesV2Enabled = createSelector(
  getCurrentTenant,
  currentTenant => {
    if (currentTenant !== 'loading' && currentTenant !== null) {
      return currentTenant.tenant_setting?.repair_messages_v2_enabled ?? false;
    }
    return false;
  }
);

export const getAiMemberSearchEnabled = createSelector(
  getCurrentTenant,
  currentTenant => {
    if (currentTenant !== 'loading' && currentTenant !== null) {
      return currentTenant.tenant_setting?.ai_member_search_enabled ?? false;
    }
    return false;
  }
);

// New posts start published for the author's settlement when the tenant turns
// it on, otherwise for everyone. Memoized, so callers copy it before mutating.
export const getDefaultArticleScope = createSelector(
  getCurrentUser,
  getCurrentTenantSettings,
  (currentUser, tenantSettings): ArticleScope =>
    tenantSettings?.publish_to_own_settlement_by_default &&
    currentUser?.settlement
      ? {
          groups: [],
          settlements: [
            {
              id: currentUser.settlement.id,
              title: currentUser.settlement.title
            }
          ],
          publish_globally: false
        }
      : { groups: [], settlements: [], publish_globally: true }
);
